import React, { useState } from "react";
import { Modal, Typography, Tag, Divider, List, Avatar, Card, Button, message, Popconfirm, Collapse, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import moment from "moment";
import { CopyOutlined } from "@ant-design/icons";
import TextAnexo from "../../components/text-anexo";
import {
  CancelarAssinatura,
  fetchEnvelopesLogs,
  reenviarEmails,
} from "../../store/assinatura";
import unwrapAxiosPromise from "../../util/unwrapAxiosPromise";
import useDispatchWithResult from "../../hooks/useDispatchWithResult";
import styles from "./_styles.less";

const { Text } = Typography;
const DATE_FORMAT_DISPLAY = "DD/MM/YYYY";

const tipoLabels = {
  PADRAO: "Padrão",
  BIOMETRICA: "Biométrica",
  CERTIFICADO: "Certificado",
};

export default function AssinaturaModal({ open, doc, onClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatchWithResult();
  const [logs, setLogs] = useState();

  const isDisabled = doc?.status === "CANCELADO";

  const handleReenviar = async () => {
    const diffInMonths = dayjs().diff(dayjs(doc.created_at), "month");
    const isExpired = diffInMonths >= 1;
    if (isExpired) {
      message.warning(t("Links expirados!"));
      return;
    }

    unwrapAxiosPromise(dispatch(reenviarEmails({ envelope_id: doc.id })))
      .then(() => {
        message.success(t("Reenvio realizado com sucesso."));
        onClose(true);
      });
  };

  const handleCancelar = async () => {
    unwrapAxiosPromise(dispatch(CancelarAssinatura({ envelope_id: doc.id })))
      .then(() => {
        message.success(t("Envio de assinaturas Cancelado."));
        onClose(true);
      });
  };

  const handleLogs = async () => {
    unwrapAxiosPromise(dispatch(fetchEnvelopesLogs({ id: doc.id })))
      .then((response) => {
        setLogs(response);
      });
  };

  return (
    <Modal
      title={doc ? doc.nome : ""}
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      {doc && (
        <>
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div>
                <Text strong>{t("Criado em")}: </Text>
                {dayjs(doc.created_at).format(`${DATE_FORMAT_DISPLAY} HH:mm`)}
              </div>
              <div>
                <Text strong>{t("Válido até")}: </Text>
                {dayjs(doc.created_at).add(30, "day").format(`${DATE_FORMAT_DISPLAY} HH:mm`)}
              </div>
            </div>

            <div>
              <Text strong>{t("Status")}: </Text>
              <Tag color={doc.status === "CANCELADO" ? "red" : doc.assinado ? "green" : "orange"}>
                {t(doc.status === "CANCELADO" ? "Cancelado" : doc.assinado ? "Concluído" : "Pendente")}
              </Tag>
            </div>
          </div>

          {doc.responsavel && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t("Criado por:")} </Text>
              {doc.responsavel}
            </div>
          )}

          {doc.origem_envelope && (() => {
            const parts = doc.origem_envelope.split("/");
            const acesso = parts[0] || "";
            const empresa = parts[1] || "";
            const aba = parts[2] || "";
            return (
              <div style={{ marginBottom: 16, display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                  <Text strong>{t("Acesso")}: </Text>
                  {acesso}
                </div>
                <div>
                  <Text strong>{t("Empresa")}: </Text>
                  {empresa}
                </div>
                <div>
                  <Text strong>{t("Aba")}: </Text>
                  {aba}
                </div>
              </div>
            );
          })()}

          <Divider />
          <Text strong>{t("Assinantes")}</Text>
          <Card
            size="small"
            style={{ background: "#fafafa", marginTop: 8 }}
            bodyStyle={{ paddingRight: 20 }}
            extra={
              <div style={{ display: "flex", gap: 8 }}>
                <Popconfirm
                  title={t("Cancelar envio?")}
                  description={t("Isso irá cancelar o envio deste envelope.")}
                  onConfirm={handleCancelar}
                  okText={t("Sim")}
                  cancelText={t("Não")}
                  disabled={isDisabled}
                >
                  <Button danger disabled={isDisabled}>
                    {t("Cancelar envio assinatura")}
                  </Button>
                </Popconfirm>

                <Popconfirm
                  title={t("Reenviar para assinatura?")}
                  description={t("Isto reenviará o e-mail para os assinantes pendentes.")}
                  onConfirm={handleReenviar}
                  okText={t("Sim")}
                  cancelText={t("Não")}
                  disabled={isDisabled}
                >
                  <Button type="primary" disabled={isDisabled}>
                    {t("Reenviar para assinatura")}
                  </Button>
                </Popconfirm>
              </div>
            }
          >
            <List
              dataSource={doc.assinaturas}
              split={false}
              renderItem={(item) => (
                <List.Item>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Avatar>{item.nome ? item.nome[0].toUpperCase() : "U"}</Avatar>
                      <div>
                        <div>{item.nome}</div>
                        <div style={{ fontSize: 12, color: "#888" }}>{item.email}</div>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Tag color="blue">{tipoLabels[item.tipo_assinatura] || item.tipo_assinatura}</Tag>
                      <Tag color={item.assinado ? "green" : "orange"}>
                        {item.assinado ? t("Assinado") : t("Pendente")}
                      </Tag>

                      {item.link_assinatura && !isDisabled && (() => {
                        const diffInMonths = dayjs().diff(dayjs(doc.created_at), "month");
                        const isExpired = diffInMonths >= 1;

                        return (
                          <CopyOutlined
                            onClick={() => {
                              if (isExpired) {
                                message.warning(t("Este link está expirado e não pode ser copiado."));
                                return;
                              }
                              navigator.clipboard.writeText(item.link_assinatura);
                              message.success(t("Link copiado!"));
                            }}
                            style={{
                              cursor: isExpired ? "not-allowed" : "pointer",
                              color: isExpired ? "#ccc" : "#1890ff",
                              fontSize: 18,
                              opacity: isExpired ? 0.5 : 1,
                              padding: 4,
                            }}
                          />
                        );
                      })()}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>

          <Collapse
            bordered
            defaultActiveKey={[]}
            onChange={handleLogs}
            style={{ marginTop: 24, marginBottom: 24 }}
          >
            <Collapse.Panel header={t("Logs")} key="1">
              <div className={styles["logs"]}>
                {logs?.length ? (
                  logs.map((log) => (
                    <div key={log.id} style={{ marginBottom: 8 }}>
                      <div>{log.texto}</div>
                      <div className={styles["log-autor"]} style={{ fontSize: 12, color: "#666" }}>
                        <Tooltip title={moment(log.createdAt).format("DD/MM/YYYY HH:mm")}>
                          {moment(log.createdAt).format("DD/MM/YYYY HH:mm")}
                        </Tooltip>
                        {log.usuario?.nome && (
                          <div>
                            {t("por")} <strong>{log.usuario.nome}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: "#888" }}>{t("Nenhum log encontrado.")}</div>
                )}
              </div>
            </Collapse.Panel>
          </Collapse>

          <Text strong>{t("Anexos")}</Text>
          <Card size="small" style={{ background: "#fafafa", marginTop: 8 }}>
            <TextAnexo label={t("Anexo original")} value={doc.anexo_origem} />
            <TextAnexo label={t("Anexo assinado")} value={doc.anexo_assinado} />
          </Card>
        </>
      )}
    </Modal>
  );
}
